import { createContextApi, CACHE_POLICY, providesList, createCursorPagedEndpoint, providesOne, emitPlatformEvent, invalidatesOne, registerCrossContextInvalidation } from '@quickbee/store';
import { getContextClient } from '@quickbee/api-client';

// src/index.ts
var redemptionMocksLoader = () => import('./mocks-UONGF6IS.js').then((module) => module.redemptionMocks);
function redemptionClient() {
  return getContextClient({ context: "redemption", mocks: redemptionMocksLoader });
}
var REDEMPTION_TAGS = ["Redemption", "RedeemableBalance", "Coupon"];
var certificateDownloadRequest = (redemptionId) => ({
  url: `/redemptions/${redemptionId}/certificate`,
  method: "GET",
  responseType: "binary"
});
var redemptionEndpoints = (build) => ({
  listRedemptions: build.query({
    query: (params) => ({ url: "/redemptions", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Redemption", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getRedemption: build.query({
    query: (id) => ({ url: `/redemptions/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Redemption", id)
  }),
  getRedeemableBalances: build.query({
    query: () => ({ url: "/redemptions/redeemable-balances" }),
    providesTags: [{ type: "RedeemableBalance", id: "LIST" }],
    // A balance the customer is about to redeem against, priced at the live rate.
    keepUnusedDataFor: CACHE_POLICY.financialBalance.keepUnusedDataFor
  }),
  /**
   * ADR-008 step 1 — **reserves only**. For `MERCHANT_COUPON` no gold moves and
   * nothing is priced: the response comes back `RESERVED` with a null
   * `redemptionValue`. For `BANK_TRANSFER` the escrow debit does happen here
   * (ADR-005), which is why the redeemable balance is invalidated either way.
   */
  createRedemption: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/redemptions",
      method: "POST",
      body,
      // Keyed on the intent, not the attempt: a customer who taps "Redeem"
      // twice reserves once.
      idempotencyKey: `redeem:${body.schemeEnrollmentId}:${draftId}`
    }),
    invalidatesTags: [
      { type: "Redemption", id: "LIST" },
      { type: "RedeemableBalance", id: "LIST" }
    ],
    async onQueryStarted(_args, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("redemption.requested", { redemptionId: data.id });
      } catch {
      }
    }
  }),
  /**
   * ADR-008 step 2 — **the buy-back**. Prices the gold at the live rate, debits
   * it, and returns the issued coupon carrying the now-frozen rupee value.
   *
   * Returns a `CouponResponse`, not the parent request: the request must be
   * re-read (or its tag invalidated, as here) to see the new
   * `AWAITING_REDEMPTION` status. Repeat calls are safe — the handler
   * short-circuits and re-renders the existing coupon rather than debiting
   * again — but the redeemable balance genuinely changed, so it is invalidated.
   */
  generateCertificate: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/redemptions/${id}/certificate`,
      method: "POST",
      body,
      idempotencyKey: `redemption-certificate:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      { type: "Coupon", id: "LIST" },
      { type: "RedeemableBalance", id: "LIST" }
    ]
  }),
  cancelRedemption: build.mutation({
    query: ({ id, reason }) => ({
      url: `/redemptions/${id}/cancel`,
      method: "POST",
      // The body is optional server-side, but `reason` is the only thing in it —
      // sending `{}` keeps one request shape instead of two.
      body: reason === void 0 ? {} : { reason },
      idempotencyKey: `redemption-cancel:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      { type: "RedeemableBalance", id: "LIST" }
    ]
  }),
  /** Ops action. `:tenant`-scoped — takes no body at all. */
  approveRedemption: build.mutation({
    query: ({ id }) => ({
      url: `/redemptions/${id}/approve`,
      method: "POST",
      idempotencyKey: `redemption-approve:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Redemption", id)
  }),
  /** Ops action. `reason` is required — the server rejects a blank one. */
  rejectRedemption: build.mutation({
    query: ({ id, reason, reasonCode }) => ({
      url: `/redemptions/${id}/reject`,
      method: "POST",
      // `reasonCode` is optional (B10) and omitted rather than sent as `undefined`, so
      // nothing depends on how the transport happens to serialise an absent field.
      body: reasonCode === void 0 ? { reason } : { reason, reasonCode },
      idempotencyKey: `redemption-reject:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      // A reject reverses the ledger debit wherever one exists (INV-11).
      { type: "RedeemableBalance", id: "LIST" }
    ]
  }),
  completeRedemption: build.mutation({
    query: ({ id }) => ({
      url: `/redemptions/${id}/complete`,
      method: "POST",
      idempotencyKey: `redemption-complete:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      { type: "RedeemableBalance", id: "LIST" }
    ],
    async onQueryStarted({ id }, { queryFulfilled }) {
      try {
        await queryFulfilled;
        emitPlatformEvent("redemption.settled", { redemptionId: id });
      } catch {
      }
    }
  }),
  // ─── Coupons (POS / merchant counter) ───────────────────────────────────────
  /**
   * A read dressed as a `POST` so the code never lands in a URL or an access
   * log. Not `@IdempotentRequest` server-side, so it must opt out — and a
   * not-currently-redeemable coupon comes back `200 OK` with `valid: false`,
   * never as a thrown error.
   */
  verifyCoupon: build.mutation({
    query: ({ code }) => ({
      url: "/redemptions/coupons/verify",
      method: "POST",
      body: { code },
      skipIdempotencyKey: true
    })
  }),
  /**
   * `202 Accepted` with an empty envelope — this guarantees the send was
   * *initiated*, not delivered. Deliberately **not** idempotency-keyed: a resend
   * is a legitimate repeat action, and the abuse control is the server's own
   * resend cooldown (a `429`), not a swallowed duplicate.
   */
  sendCouponOtp: build.mutation({
    query: ({ code }) => ({
      url: `/redemptions/coupons/${code}/otp`,
      method: "POST",
      skipIdempotencyKey: true
    })
  }),
  /**
   * The money-moving action at the counter, and the most realistic double-tap
   * hazard in the platform. Invalidates by the coupon's **id** taken off the
   * response — the caller only holds a code, and the cache is keyed by id.
   */
  redeemCoupon: build.mutation({
    query: ({ code, otpCode, storeId }) => ({
      url: `/redemptions/coupons/${code}/redeem`,
      method: "POST",
      body: storeId === void 0 ? { otpCode } : { otpCode, storeId },
      idempotencyKey: `coupon-redeem:${code}`
    }),
    invalidatesTags: (result) => [
      { type: "Coupon", id: "LIST" },
      { type: "Redemption", id: "LIST" },
      ...result ? [{ type: "Coupon", id: result.id }] : [],
      ...result ? invalidatesOne("Redemption", result.redemptionRequestId) : []
    ],
    async onQueryStarted(_args, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("redemption.settled", { redemptionId: data.redemptionRequestId });
      } catch {
      }
    }
  }),
  getCoupon: build.query({
    query: (id) => ({ url: `/redemptions/coupons/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Coupon", id)
  }),
  listCoupons: build.query({
    query: (params) => ({ url: "/redemptions/coupons", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Coupon", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  })
});

// src/certificate.ts
function downloadRedemptionCertificate(redemptionId) {
  return redemptionClient().request(certificateDownloadRequest(redemptionId));
}

// src/guards.ts
var TERMINAL_REDEMPTION_STATUSES = [
  "COMPLETED",
  "REJECTED",
  "CANCELLED"
];
function isTerminalRedemptionStatus(status) {
  return TERMINAL_REDEMPTION_STATUSES.includes(status);
}
function isCancellableRedemptionStatus(status) {
  return status === "RESERVED" || status === "PENDING";
}
function canGenerateCertificate(status) {
  return status === "RESERVED";
}
var COUPON_CODE_PATTERN = /^CPN-[0-9A-Z]{4,10}$/;
function isCouponCode(value) {
  return COUPON_CODE_PATTERN.test(value);
}
var COUPON_OTP_PATTERN = /^[0-9]{6}$/;
function isCouponOtp(value) {
  return COUPON_OTP_PATTERN.test(value);
}

// src/index.ts
var redemptionApi = createContextApi({
  context: "redemption",
  reducerPath: "redemptionApi",
  tagTypes: REDEMPTION_TAGS,
  mocks: redemptionMocksLoader,
  endpoints: redemptionEndpoints
});
var REDEMPTION_INVALIDATION_RULES = [
  { event: "ledger.posted", invalidate: () => [{ type: "RedeemableBalance", id: "LIST" }] },
  { event: "payment.captured", invalidate: () => [{ type: "RedeemableBalance", id: "LIST" }] }
];
function registerRedemptionInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: redemptionApi,
    rules: REDEMPTION_INVALIDATION_RULES
  });
}
var {
  useListRedemptionsQuery,
  useLazyListRedemptionsQuery,
  useGetRedemptionQuery,
  useLazyGetRedemptionQuery,
  useGetRedeemableBalancesQuery,
  useCreateRedemptionMutation,
  useGenerateCertificateMutation,
  useCancelRedemptionMutation,
  useApproveRedemptionMutation,
  useRejectRedemptionMutation,
  useCompleteRedemptionMutation,
  useVerifyCouponMutation,
  useSendCouponOtpMutation,
  useRedeemCouponMutation,
  useGetCouponQuery,
  useListCouponsQuery,
  useLazyListCouponsQuery
} = redemptionApi;

export { COUPON_CODE_PATTERN, COUPON_OTP_PATTERN, REDEMPTION_INVALIDATION_RULES, REDEMPTION_TAGS, TERMINAL_REDEMPTION_STATUSES, canGenerateCertificate, downloadRedemptionCertificate, isCancellableRedemptionStatus, isCouponCode, isCouponOtp, isTerminalRedemptionStatus, redemptionApi, registerRedemptionInvalidation, useApproveRedemptionMutation, useCancelRedemptionMutation, useCompleteRedemptionMutation, useCreateRedemptionMutation, useGenerateCertificateMutation, useGetCouponQuery, useGetRedeemableBalancesQuery, useGetRedemptionQuery, useLazyGetRedemptionQuery, useLazyListCouponsQuery, useLazyListRedemptionsQuery, useListCouponsQuery, useListRedemptionsQuery, useRedeemCouponMutation, useRejectRedemptionMutation, useSendCouponOtpMutation, useVerifyCouponMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map